import type { PlanungBoard } from "@/lib/montageplanung";

/** Aktualisiert bestehende Einträge und fügt neue hinzu (gleiche ID = übernehmen). */
function mergeByKey<T>(existing: T[], incoming: T[], keyOf: (item: T) => string | number): T[] {
  const incomingMap = new Map(incoming.map((item) => [keyOf(item), item]));
  const result: T[] = [];
  const seen = new Set<string | number>();
  for (const item of existing) {
    const key = keyOf(item);
    result.push(incomingMap.get(key) ?? item);
    seen.add(key);
  }
  for (const item of incoming) {
    const key = keyOf(item);
    if (!seen.has(key)) {
      result.push(item);
      seen.add(key);
    }
  }
  return result;
}

function overlapsDateRange(
  dateFrom: string,
  dateTo: string,
  rangeFrom: string,
  rangeTo: string,
): boolean {
  const from = dateFrom.slice(0, 10);
  const to = dateTo.slice(0, 10);
  return from <= rangeTo && to >= rangeFrom;
}

/** Fügt einen nachgeladenen Datums-Abschnitt in den bestehenden Planungsstand ein. */
export function mergePlanungBoard(prev: PlanungBoard | null, chunk: PlanungBoard): PlanungBoard {
  if (!prev) return chunk;

  return {
    ...prev,
    undo_available: chunk.undo_available,
    undo_summary: chunk.undo_summary,
    assignments: mergeByKey(prev.assignments, chunk.assignments, (a) => a.id),
    absences: mergeByKey(prev.absences ?? [], chunk.absences ?? [], (a) => a.id),
    calendar_days: mergeByKey(
      prev.calendar_days ?? [],
      chunk.calendar_days ?? [],
      (d) => d.work_date.slice(0, 10),
    ),
    unavailabilities: mergeByKey(
      prev.unavailabilities ?? [],
      chunk.unavailabilities ?? [],
      (u) => u.id,
    ),
  };
}

/**
 * Ersetzt alle Einträge, die den Auffrischungs-Zeitraum überlappen, durch den
 * frischen Chunk – für Live-Updates ohne den gesamten akkumulierten Range neu
 * zu laden.
 */
export function replacePlanungBoardRange(
  prev: PlanungBoard | null,
  chunk: PlanungBoard,
  rangeFromIso: string,
  rangeToIso: string,
): PlanungBoard {
  if (!prev) return chunk;
  const from = rangeFromIso.slice(0, 10);
  const to = rangeToIso.slice(0, 10);

  return {
    ...chunk,
    date_from: prev.date_from,
    date_to: prev.date_to,
    assignments: [
      ...prev.assignments.filter(
        (a) => !overlapsDateRange(a.date_from, a.date_to, from, to),
      ),
      ...chunk.assignments,
    ],
    absences: [
      ...(prev.absences ?? []).filter(
        (a) => !overlapsDateRange(a.date_from, a.date_to, from, to),
      ),
      ...(chunk.absences ?? []),
    ],
    calendar_days: [
      ...(prev.calendar_days ?? []).filter((d) => {
        const day = d.work_date.slice(0, 10);
        return day < from || day > to;
      }),
      ...(chunk.calendar_days ?? []),
    ],
    unavailabilities: [
      ...(prev.unavailabilities ?? []).filter(
        (u) => !overlapsDateRange(u.date_from, u.date_to, from, to),
      ),
      ...(chunk.unavailabilities ?? []),
    ],
  };
}
